import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useConfirm } from '@/shared/ui/ConfirmProvider'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { Button } from '@/shared/ui/Button'
import { SlideOver } from '@/shared/ui/SlideOver'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { Plus, Edit, Trash2, Loader2, Search, Car, Bus, Truck } from 'lucide-react'
import { useDebounce } from '@/shared/hooks'
import { useCreateUpdateCategory, useDeleteCategory } from '@/api/hooks'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { cn } from '@/shared/utils'
import { Can } from '@/shared/ui/Can'

// ── Helpers ────────────────────────────────────────────────────────────────
const trName = (c: any, loc: string) =>
  c?.category_translations?.find((t: any) => t?.locale?.toLowerCase() === loc)?.name

const enName = (c: any) => trName(c, 'en') ?? c?.category_translations?.[0]?.name ?? c?.name ?? '—'

// pick an icon based on the english name (purely cosmetic, matches old design)
function CategoryIcon({ name }: { name: string }) {
  const n = (name || '').toLowerCase()
  const Icon = n.includes('bus') || n.includes('heavy') ? Bus
    : n.includes('truck') || n.includes('lorry') ? Truck
    : Car
  return <Icon size={56} strokeWidth={1.5} className="text-[#f68b2c]" />
}

export function CategoryPage() {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('') // '' | '1' | '0'
  const debounced = useDebounce(search)

  // ── List (get-list returns full category_translations + parent + status) ──
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['vehicle-categories', debounced],
    queryFn: () => restClient
      .get(`${endpoints.users.listCategory}?page=1&limit=1000${debounced ? `&search=${encodeURIComponent(debounced)}` : ''}`)
      .then(r => r.data),
    staleTime: 30_000,
  })

  const save = useCreateUpdateCategory()
  const del  = useDeleteCategory()

  const allRows: any[] = useMemo(() => (Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []), [data])

  // id → category lookup for resolving parent names
  const byId = useMemo(() => {
    const m: Record<string, any> = {}
    allRows.forEach(c => { m[String(c.id)] = c })
    return m
  }, [allRows])

  const parentName = (c: any) => {
    const parent = c?.parent_category ?? c?.parentCategory ?? byId[String(c?.parent_category_id ?? c?.parent_id ?? '')]
    return parent ? enName(parent) : '—'
  }

  const isActive = (c: any) => c?.is_active === true || c?.is_active === 1 || c?.status === 1 || c?.status === true

  // parent options for the form (categories that can be parents)
  const parentOptions = allRows

  const rows = useMemo(() => allRows.filter(c => {
    if (statusFilter === '1') return isActive(c)
    if (statusFilter === '0') return !isActive(c)
    return true
  }), [allRows, statusFilter])

  // ── Modal state ──
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<any | null>(null)
  const [form, setForm] = useState({ name: '', name_ar: '', name_ur: '', parent_category_id: '', is_active: true })
  const [toDelete, setToDelete] = useState<any | null>(null)
  const [togglingId, setTogglingId] = useState<number | null>(null)

  const openNew = () => {
    setEditing(null)
    setForm({ name: '', name_ar: '', name_ur: '', parent_category_id: '', is_active: true })
    setOpen(true)
  }
  const openEdit = (c: any) => {
    setEditing(c)
    setForm({
      name: enName(c) === '—' ? '' : enName(c),
      name_ar: trName(c, 'ar') ?? '',
      name_ur: trName(c, 'ur') ?? '',
      parent_category_id: String(c?.parent_category_id ?? c?.parent_id ?? ''),
      is_active: isActive(c),
    })
    setOpen(true)
  }

  const buildPayload = (f: typeof form, id?: number) => {
    const payload: Record<string, unknown> = {
      name: f.name,
      name_ar: f.name_ar,
      name_ur: f.name_ur,
      is_active: f.is_active ? 1 : 0,
    }
    if (f.parent_category_id) payload.parent_category_id = Number(f.parent_category_id)
    if (id) payload.id = id
    return payload
  }

  const handleSave = async () => {
    if (!form.name.trim()) return
    if (editing?.id && !(await confirm({
      title: t('common.confirmSaveTitle'), description: t('common.confirmSaveDescription'),
      confirmLabel: t('common.saveChanges'), variant: 'primary',
    }))) return
    await save.mutateAsync(buildPayload(form, editing?.id))
    setOpen(false)
    refetch()
  }

  const handleToggle = async (c: any) => {
    setTogglingId(c.id)
    try {
      await save.mutateAsync(buildPayload({
        name: enName(c) === '—' ? '' : enName(c),
        name_ar: trName(c, 'ar') ?? '',
        name_ur: trName(c, 'ur') ?? '',
        parent_category_id: String(c?.parent_category_id ?? c?.parent_id ?? ''),
        is_active: !isActive(c),
      }, c.id))
      refetch()
    } finally {
      setTogglingId(null)
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader title="Vehicle Category List"
        breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Vehicle Category' }]}
      />

      <Card padding={false}>
        {/* ── Toolbar ── */}
        <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-bold text-gray-900">Vehicle Category List</h2>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-500 bg-white min-w-32"
            >
              <option value="">Select Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
            <div className="relative min-w-48">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name"
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
            </div>
            <Can perm="POST /admin/category/*"><Button icon={<Plus size={14} />} size="sm" onClick={openNew}>Create New Category</Button></Can>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="p-5">
          {isError ? (
            <p className="py-12 text-center text-sm text-red-500">Failed to load categories.</p>
          ) : isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-500" size={24} /></div>
          ) : rows.length === 0 ? (
            <p className="py-12 text-center text-sm text-gray-400">No categories found</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {rows.map(c => {
                const active = isActive(c)
                return (
                  <div key={c.id} className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden bg-white">
                    {/* Icon header */}
                    <div className="relative bg-[#fdeee2] h-40 flex items-center justify-center">
                      {c.image || c.icon || c.category_image ? (
                        <img src={c.image ?? c.icon ?? c.category_image} alt={enName(c)} className="h-24 object-contain" />
                      ) : (
                        <CategoryIcon name={enName(c)} />
                      )}
                      {/* Action buttons */}
                      <div className="absolute top-3 right-3 flex items-center gap-2">
                        <button
                          onClick={() => handleToggle(c)}
                          disabled={togglingId === c.id}
                          title={active ? 'Deactivate' : 'Activate'}
                          className={cn('w-9 h-5 rounded-full relative transition-colors flex-shrink-0',
                            active ? 'bg-[#f68b2c]' : 'bg-gray-300', togglingId === c.id && 'opacity-60')}
                        >
                          <span className={cn('absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all', active ? 'left-[18px]' : 'left-0.5')} />
                        </button>
                        <Can perm="DELETE /admin/category/*">
                        <button onClick={() => setToDelete(c)}
                          className="w-7 h-7 rounded-lg bg-white/80 flex items-center justify-center text-red-500 hover:bg-white shadow-sm">
                          <Trash2 size={14} />
                        </button>
                        </Can>
                        <Can perm="POST /admin/category/*">
                        <button onClick={() => openEdit(c)}
                          className="w-7 h-7 rounded-lg bg-gray-900 flex items-center justify-center text-white hover:bg-gray-700 shadow-sm">
                          <Edit size={13} />
                        </button>
                        </Can>
                      </div>
                    </div>

                    {/* Details */}
                    <div>
                      <Row label="En" value={enName(c)} />
                      <Row label="Ar" value={trName(c, 'ar') ?? '—'} rtl />
                      <Row label="Ur" value={trName(c, 'ur') ?? '—'} rtl />
                      <div className="flex items-center justify-between px-4 py-2.5 bg-[#fdf3e9]">
                        <span className="text-xs text-gray-500">Parent Category</span>
                        <span className="text-xs font-bold text-gray-900">{parentName(c)}</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </Card>

      {/* ── Create / Edit ── */}
      <SlideOver open={open} onClose={() => setOpen(false)} title={editing ? 'Edit Category' : 'Create New Category'}>
        <div className="space-y-4">
          <Field label="Name (English) *" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="e.g. Car" />
          <Field label="Name (Arabic)" value={form.name_ar} onChange={v => setForm(f => ({ ...f, name_ar: v }))} rtl />
          <Field label="Name (Urdu)" value={form.name_ur} onChange={v => setForm(f => ({ ...f, name_ur: v }))} rtl />
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">Parent Category</label>
            <select
              value={form.parent_category_id}
              onChange={e => setForm(f => ({ ...f, parent_category_id: e.target.value }))}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white"
            >
              <option value="">None</option>
              {parentOptions.filter(p => p.id !== editing?.id).map(p => (
                <option key={p.id} value={p.id}>{enName(p)}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center justify-between py-1">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Active</span>
            <button type="button" onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
              className={cn('w-10 h-5 rounded-full relative transition-colors', form.is_active ? 'bg-[#f68b2c]' : 'bg-gray-300')}>
              <span className={cn('absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all', form.is_active ? 'left-5' : 'left-0.5')} />
            </button>
          </label>
          <Button className="w-full justify-center" loading={save.isPending} onClick={handleSave}>Save</Button>
        </div>
      </SlideOver>

      <ConfirmDialog open={!!toDelete} onClose={() => setToDelete(null)}
        onConfirm={async () => { if (toDelete) { await del.mutateAsync(toDelete.id); refetch() } setToDelete(null) }}
        title="Delete category?" description={`This will remove "${toDelete ? enName(toDelete) : ''}". This cannot be undone.`} loading={del.isPending} />
    </div>
  )
}

function Row({ label, value, rtl }: { label: string; value: string; rtl?: boolean }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-50">
      <span className="text-xs text-gray-400">{label}</span>
      <span className={cn('text-sm font-semibold text-gray-900', rtl && 'text-right')} dir={rtl ? 'rtl' : 'ltr'}>{value}</span>
    </div>
  )
}

function Field({ label, value, onChange, placeholder, rtl }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; rtl?: boolean }) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">{label}</label>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} dir={rtl ? 'rtl' : 'ltr'}
        className={`w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 ${rtl ? 'text-right' : ''}`} />
    </div>
  )
}
